import { useEffect, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { useLocation } from 'react-router';

import { useStateKey } from './useStateKey.hook';
import { useUserGroups } from './useUserGroups';
import {
  useGetSellerByHandle,
  useGetSellerByStateKey,
} from '../api/lending/lapiHooks';
import { getUserRoles } from '../utils/sellerUserRoles';

const getPageName = (pathname: string): string =>
  pathname.replace(/^\/pay\//, '').replace(/\/$/, '') || 'dashboard';

export const useTrackSellerDashboardPageView = () => {
  const { pathname } = useLocation();
  const { stateKey } = useStateKey();
  const { groups } = useUserGroups();
  // seller_type (segment) + handle come from the by-state-key endpoint; the
  // seller_id uuid (consistent with other tracking events) is only on the
  // by-handle endpoint, so chain the handle into that lookup.
  const { seller: sellerByStateKey, loadingSeller: loadingSellerByStateKey } =
    useGetSellerByStateKey();
  const sellerHandle = sellerByStateKey?.data.handle;
  const { seller: sellerByHandle, loadingSeller: loadingSellerByHandle } =
    useGetSellerByHandle(sellerHandle);

  const lastTrackedPathname = useRef<string | null>(null);

  useEffect(() => {
    // Wait on both seller queries so the first event for a page carries
    // seller_id (by-handle) and seller_segment (by-state-key). Both settling
    // is enough — we deliberately don't gate on query errors: a persistent
    // error should still record the page view with whatever we have (missing
    // seller_id is acceptable) rather than drop it entirely. Note the by-handle
    // query stays disabled (and so loadingSellerByHandle stays true) until a
    // handle resolves, so an unonboarded seller with no handle won't fire an
    // event — fine here, since this hook only mounts for onboarded dashboard
    // sellers via RequireAuthenticatedSeller.
    if (!stateKey || loadingSellerByStateKey || loadingSellerByHandle) return;
    if (lastTrackedPathname.current === pathname) return;
    lastTrackedPathname.current = pathname;

    tracking.ampli.sellerDashboardPageViewed({
      page_name: getPageName(pathname),
      seller_id: sellerByHandle?.seller_id,
      seller_segment: sellerByStateKey?.data.seller_type,
      user_role: getUserRoles(groups),
    });
  }, [
    pathname,
    stateKey,
    loadingSellerByStateKey,
    loadingSellerByHandle,
    groups,
    sellerByHandle,
    sellerByStateKey,
  ]);
};
